import React, { useCallback, useEffect, useMemo } from 'react'
import dayjs from 'dayjs'
import { NumberFmt, measureUnit } from 'src/utils'
import { useTheme } from 'styled-components'
import { useDispatch } from 'react-redux'
import { actions } from '../../../ReceivementState'
import * as S from '../styles'
import { ProductStatusEnum, SalesReceivementBatch, SalesReceivementProduct } from '../../../ReceivementTypes'
import { ActionDropdown, dropdownOptions } from './ActionDropdown'
import { AcceptQuantityInput } from './AcceptQuantityInput'
import { ReturnQuantityInput } from './ReturnQuantityInput'
import { SwapQuantityInput } from './SwapQuantityInput'
import { useSelector } from 'src/utils/store'
import { selectArStockDaysSumByProduct } from 'src/features/receivement/ReceivementSelectors'

interface Props {
  batch: SalesReceivementBatch;
  productStatus: ProductStatusEnum;
  product: SalesReceivementProduct;
  allowProductExchange?: boolean | null;
}

const ProductTableRow = React.memo(({
  batch, productStatus, product, allowProductExchange,
}: Props) => {
  const batchState = useSelector(
    (state) => state.receivement.batchesStateByBatchItemOrderIds[batch.idsBatchItemOrder.join(',')],
  )

  const arStockDaysSumSelector = useMemo(
    () => selectArStockDaysSumByProduct(product.modelId, product.idReceivementAuthorization),
    [product.modelId, product.idReceivementAuthorization],
  )
  const arStockDaysSum = useSelector(arStockDaysSumSelector)

  const theme = useTheme()
  const dispatch = useDispatch()

  const [quantityErrorMessage, setQuantityErrorMessage] = React.useState<string | undefined>('')

  const isValidQuantity = useCallback((quantity) => quantity > 0 && quantity <= batch.totalQuantity, [batch.totalQuantity])

  const handleErrorQuantity = useCallback((value: number, option?: string) => {
    if (option === dropdownOptions.PARTIAL && !isValidQuantity(value)) {
      setQuantityErrorMessage('Valor inválido!')
      dispatch(actions.addBatchErrorId(batch.idsBatchItemOrder.toString()))
    } else {
      setQuantityErrorMessage(undefined)
      dispatch(actions.removeBatchErrorId(batch.idsBatchItemOrder.toString()))
    }
  }, [batch.idsBatchItemOrder, isValidQuantity, dispatch])

  React.useEffect(() => {
    const option = batchState.option ? dropdownOptions[batchState.option] : undefined
    handleErrorQuantity(batchState.acceptedQuantity, option)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (!batchState.option) return
    if (batchState.option !== 'PARTIAL' || isValidQuantity(batchState.acceptedQuantity)) {
      setQuantityErrorMessage(undefined)
      dispatch(actions.removeBatchErrorId(batch.idsBatchItemOrder.toString()))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchState.option, batchState.acceptedQuantity])

  const verifyExpirationDate = batch.minExpirationDate && batch.expirationDate && batch.expirationDate < batch.minExpirationDate

  const minExpirationDateEmpty = batch.minExpirationDate === null || batch.minExpirationDate === '' || !batch.minExpirationDate

  const emptyExpirationDate = batch.expirationDate === null || batch.expirationDate === '' || !batch.expirationDate

  const getValidateByBatch = () => {
    const expirationDate = batch.expirationDate ? dayjs(batch.expirationDate).format('DD/MM/YYYY') : '---'
    const backgroundColor = (!emptyExpirationDate && dayjs(batch.expirationDate).startOf('day').isBefore(dayjs().startOf('day')))
      ? theme.colors.red
      : verifyExpirationDate
        ? theme.colors.orange
        : emptyExpirationDate || (batch.salesData === null && minExpirationDateEmpty)
          ? 'inerit'
          : (batch.salesData === true && minExpirationDateEmpty) || (batch.salesData === false && minExpirationDateEmpty)
            ? theme.colors.black
            : 'inerit'
    const color = emptyExpirationDate || (batch.salesData === null && minExpirationDateEmpty)
      ? theme.colors.darkGray
      : (verifyExpirationDate) || (batch.salesData === true && minExpirationDateEmpty) || (batch.salesData === false && minExpirationDateEmpty)
        ? theme.colors.white
        : theme.colors.darkGray
    return <S.ExpirationDate style={{ backgroundColor, color }}>{expirationDate}</S.ExpirationDate>
  }

  const validateExpirationDate = () => {
    if (!emptyExpirationDate && dayjs(batch.expirationDate).startOf('day').isBefore(dayjs().startOf('day'))) {
      return 'Data de validade vencida. Quantidade sugerida de aceite é 0'
    }

    if (emptyExpirationDate || (batch.salesData === null && minExpirationDateEmpty)) {
      return ''
    }

    if (batch.salesData === true && minExpirationDateEmpty) {
      return 'SEM VENDAS'
    }

    if (verifyExpirationDate) {
      const formattedMinExpirationDate = dayjs(batch.minExpirationDate).format('DD/MM/YYYY')
      return `Quantidade sugerida de aceite de acordo com a data minima de validade (${formattedMinExpirationDate}) é de ${batch.usefulQuantity} ${measureUnit(batch.measureUnit.isKg)}`
    }

    if (batch.salesData === false && minExpirationDateEmpty) {
      return 'Não foi possível calcular a data de validade mínima para este lote'
    }
    return ''
  }

  const getStockDays = () => {
    const color = batchState.acceptedQuantity > 0 ? theme.colors.confirm : theme.colors.black
    const prefix = '+'
    const value = batchState.acceptedQuantity > 0 ? `+${batchState.stockDays?.toFixed(1)}` : '-'
    return <S.Text style={{ color }}><NumberFmt prefix={prefix} value={value}/></S.Text>
  }

  useEffect(() => {
    dispatch(actions.setTotalArStockDays({
      modelId: product.modelId,
      idReceivementAuthorization: product.idReceivementAuthorization,
      totalArStockDays: arStockDaysSum + product.currentStockDays,
    }))
  }, [arStockDaysSum, dispatch, product.modelId, product.idReceivementAuthorization, product.currentStockDays])

  return (
    <S.TableBodyRow backgroundColor={theme.colors.white}>
      <S.TableBodyCell flex={1}>
        <NumberFmt value={batch.totalQuantity} sufix={measureUnit(batch.measureUnit.isKg)} />
      </S.TableBodyCell>
      <S.TableBodyCell flex={1}>
        <NumberFmt value={batch.damageQuantity} sufix={measureUnit(batch.measureUnit.isKg)} />
      </S.TableBodyCell>
      <S.TableBodyCell flex={1}>
        <NumberFmt value={batch.usefulQuantity} sufix={measureUnit(batch.measureUnit.isKg)} />
      </S.TableBodyCell>
      <S.TableBodyCell
        flex={1}
        tooltipMessage={validateExpirationDate()}
      >
        {getValidateByBatch()}
      </S.TableBodyCell>
      <S.TableBodyCell flex={1.5}>{batch.damageType}</S.TableBodyCell>
      <S.TableBodyCell flex={1.5}>
        <ActionDropdown
          batch={batch}
          salesPerDay={product.salesPerDay}
          productStatus={productStatus}
          productIsRejected={product.isRejected}
          invoiceNumber={product.invoiceNumber}
          idReceivementAuthorization={product.idReceivementAuthorization}
          handleErrorQuantity={handleErrorQuantity}
        />
      </S.TableBodyCell>
      <S.TableBodyCell flex={1}>
        <AcceptQuantityInput
          batch={batch}
          quantityErrorMessage={quantityErrorMessage}
          handleErrorQuantity={handleErrorQuantity}
          salesPerDay={product.salesPerDay}
        />
      </S.TableBodyCell>
      <S.TableBodyCell flex={1.5}>
        <ReturnQuantityInput batch={batch} />
      </S.TableBodyCell>
      {
        allowProductExchange && (
          <S.TableBodyCell flex={1}>
            <SwapQuantityInput
              batch={batch}
            />
          </S.TableBodyCell>
        )
      }
      <S.TableBodyCell flex={1}>{getStockDays()}</S.TableBodyCell>
    </S.TableBodyRow>
  )
})

export { ProductTableRow }
